import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import { Form, FormGroup, FormLabel, FormInput, FormTextarea } from '../../components/ui/Form/Form';
import Button from '../../components/ui/Button/Button';
import Alert from '../../components/ui/Alert/Alert';
import cvData from '../../assets/data/cv-data';
import './Contact.css';

// Initialize EmailJS with your public key
emailjs.init("6Fmr0W7sCsxdE-yHS");

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFormStatus({ type: '', message: '' });

    // EmailJS service and template IDs
    const serviceId = 'service_4moo46c';
    const templateId = 'template_lwhjqjp';

    // Prepare template parameters
    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone || 'Not provided',
      subject: formData.subject || 'No subject',
      message: formData.message,
      to_name: 'Employer', // You can change this or make it dynamic
      reply_to: formData.email
    };

    try {
      const response = await emailjs.send(serviceId, templateId, templateParams);
      console.log('Email sent successfully:', response);
      
      setFormStatus({
        type: 'success',
        message: 'Thank you for your message! I will get back to you soon.'
      });
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Email sending failed:', error);
      setFormStatus({
        type: 'danger',
        message: 'Sorry, there was an error sending your message. Please try again later or contact me directly via email.'
      });
    } finally {
      setLoading(false);
    }
  };

  const { personalInfo } = cvData;

  return (
    <div className="contact-page bg-light">
      <section className="bg-primary text-white py-5 mb-5">
        <Container>
          <Row>
            <Col className="text-center">
              <h1 className="display-4 fw-bold mb-3">Get In Touch</h1>
              <p className="lead mb-0">Available for junior software engineering opportunities and collaborative projects.</p>
            </Col>
          </Row>
        </Container>
      </section>

      <Container>
        <Row className="g-4">
          <Col lg={4}>
            <Card className="border-0 shadow h-100">
              <CardBody>
                <h3 className="h5 fw-bold mb-4">Contact Information</h3>

                <div className="mb-4">
                  <div className="d-flex align-items-start mb-3">
                    <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                      <i className="fas fa-envelope text-primary"></i>
                    </div>
                    <div>
                      <p className="small text-muted mb-1">Email</p>
                      <a href={`mailto:${personalInfo.email}`} className="text-dark text-decoration-none">
                        {personalInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                      <i className="fas fa-phone text-primary"></i>
                    </div>
                    <div>
                      <p className="small text-muted mb-1">Phone</p>
                      <a href={`tel:${personalInfo.phone}`} className="text-dark text-decoration-none">
                        {personalInfo.phone}
                      </a>
                    </div>
                  </div>

                  <div className="d-flex align-items-start mb-3">
                    <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                      <i className="fas fa-map-marker-alt text-primary"></i>
                    </div>
                    <div>
                      <p className="small text-muted mb-1">Location</p>
                      <p className="text-dark mb-0">{personalInfo.location}</p>
                    </div>
                  </div>
                </div>

                <h4 className="h6 fw-bold mb-3">Follow Me</h4>
                <div className="d-flex gap-3">
                  {personalInfo.socialLinks.github && (
                    <a
                      href={personalInfo.socialLinks.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary rounded-circle p-3 d-flex align-items-center justify-content-center"
                      style={{ width: '45px', height: '45px' }}
                    >
                      <i className="fab fa-github"></i>
                    </a>
                  )}
                  {personalInfo.socialLinks.linkedin && (
                    <a
                      href={personalInfo.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-primary rounded-circle p-3 d-flex align-items-center justify-content-center"
                      style={{ width: '45px', height: '45px' }}
                    >
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                  )}
                </div>

                <div className="mt-4 pt-4 border-top">
                  <h4 className="h6 fw-bold mb-3">Working Hours</h4>
                  <ul className="list-unstyled small">
                    <li className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Monday - Friday:</span>
                      <span>9:00 AM - 5:00 PM</span>
                    </li>
                    <li className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Saturday:</span>
                      <span>By appointment</span>
                    </li>
                    <li className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Sunday:</span>
                      <span>Closed</span>
                    </li>
                  </ul>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="border-0 shadow">
              <CardBody className="p-4 p-lg-5">
                <h3 className="h5 fw-bold mb-4">Send Me a Message</h3>

                {formStatus.message && (
                  <Alert
                    variant={formStatus.type}
                    dismissible
                    onDismiss={() => setFormStatus({ type: '', message: '' })}
                    className="mb-4"
                  >
                    {formStatus.message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <FormLabel htmlFor="name">Your Name *</FormLabel>
                        <FormInput
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your full name"
                          className="form-control-lg"
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <FormLabel htmlFor="email">Email Address *</FormLabel>
                        <FormInput
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="name@example.com"
                          className="form-control-lg"
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <FormLabel htmlFor="phone">Phone Number</FormLabel>
                    <FormInput
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      className="form-control-lg"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel htmlFor="subject">Subject</FormLabel>
                    <FormInput
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="Opportunity, project, or collaboration"
                      className="form-control-lg"
                    />
                  </FormGroup>

                  <FormGroup>
                    <FormLabel htmlFor="message">Message *</FormLabel>
                    <FormTextarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell me about the role or project."
                      rows={6}
                      className="form-control-lg"
                      required
                    />
                  </FormGroup>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    className="w-100 py-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <section className="mt-5">
          <Card className="border-0 shadow overflow-hidden">
            <div className="ratio ratio-21x9">
              <iframe
                src="https://www.google.com/maps?q=Liverpool%20NSW%202176&z=12&output=embed"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                title="Location Map"
                className="w-100 h-100"
              ></iframe>
            </div>
          </Card>
        </section>

        <section className="mt-5">
          <h3 className="h4 fw-bold text-center mb-4">Frequently Asked Questions</h3>
          <Row className="g-4">
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <h4 className="h6 fw-bold mb-3">
                    <i className="fas fa-question-circle text-primary me-2"></i>
                    What is your typical response time?
                  </h4>
                  <p className="small text-muted mb-0">
                    I usually respond within 24-48 hours during business days.
                  </p>
                </CardBody>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody>
                  <h4 className="h6 fw-bold mb-3">
                    <i className="fas fa-question-circle text-primary me-2"></i>
                    What kind of roles are you looking for?
                  </h4>
                  <p className="small text-muted mb-0">
                    I am currently seeking junior software engineering opportunities where I can contribute, learn, and grow with a team.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </section>
      </Container>
    </div>
  );
};

export default Contact;